import { Component, Input, OnChanges, OnInit, SimpleChanges, AfterViewInit, OnDestroy, ViewChild, forwardRef, EventEmitter, Output } from '@angular/core';
import { NG_VALIDATORS, NG_VALUE_ACCESSOR, NgForm, ValidationErrors } from '@angular/forms';

import { Subscription } from 'rxjs';
import { debounce } from 'underscore';

import { WebcastVideoSource } from 'rev-portal/scheduledEvents/webcast/WebcastVideoSource';

import { BaseControlValueAccessor } from 'rev-shared/util/BaseControlValueAccessor';
import { DateUtil } from 'rev-shared/date/DateUtil';
import { toggleError } from 'rev-shared/util/directives/form/validation/validationUtil';

import { EditWebcastService } from './EditWebcast.Service';
import { IPresentationProfile, IVideoSource } from './EditWebcast.Contract';

import styles from './PresentationProfileInput.module.less';

@Component({
	selector: 'presentation-profile-grid-input',
	templateUrl: './PresentationProfileGridInput.Component.html',
	providers: [{
		provide: NG_VALUE_ACCESSOR,
		useExisting: forwardRef(() => PresentationProfileGridInputComponent),
		multi: true
	}, {
		provide: NG_VALIDATORS,
		useExisting: forwardRef(() => PresentationProfileGridInputComponent),
		multi: true,
	}],
	standalone: false
})
export class PresentationProfileGridInputComponent extends BaseControlValueAccessor<string> implements OnInit, OnChanges, AfterViewInit, OnDestroy {
	@Input() public webcastId: string;
	@Input() public startDate: Date;
	@Input() public endDate: Date;
	@Input() public preProductionDurationMs: number;
	@Input() public externalErrors: ValidationErrors;
	@Output() public presentationProfileSelected: EventEmitter<any> = new EventEmitter();
	@ViewChild(NgForm, { static: true }) public form: NgForm;

	public readonly WebcastVideoSource = WebcastVideoSource;
	public readonly styles = styles;
	public status: { [key: string]: boolean; } = { active: true };
	public presentationProfiles: IPresentationProfile[];
	public unfilteredProfiles: IPresentationProfile[];
	public filterAvailable: boolean = false;
	public getStartDateCallback = (): Date => this.getStartDate();

	private validateFn: () => void;
	private loadedScheduleStartDate: Date;
	private loadedScheduleEndDate: Date;
	private profilesLoaded: boolean;
	private subscription: Subscription;

	constructor(
		private readonly EditWebcastService: EditWebcastService
	) {
		super();
	}

	public ngOnInit(): void {
		this.updateSchedule();
	}

	public ngOnChanges(changes: SimpleChanges) {
		if (changes.startDate || changes.endDate || changes.preProductionDurationMs) {
			this.updateSchedule();
		}
		if (changes.externalErrors && !changes.externalErrors.isFirstChange()) {
			this.validateSelectedProfile();
		}
	}

	public ngAfterViewInit(): void {
		this.subscription = this.form.statusChanges.subscribe(() => this.validateFn?.());
	}

	public ngOnDestroy(): void {
		this.subscription?.unsubscribe();
	}

	public validate(): ValidationErrors {
		if(this.form?.invalid) {
			if (this.form?.control?.get('profileFilterAll')?.errors?.presentationProfileConflict) {
				return { presentationProfileConflict: true };
			}
			return { invalid: true };
		}
	}

	public registerOnValidatorChange(fn: () => void): void {
		this.validateFn = fn;
	}

	public onFilterChange(): void {
		this.presentationProfiles = this.unfilteredProfiles?.filter(p => !this.filterAvailable || p.available);
	}

	public onProfileSelected(profile: IPresentationProfile): void {
		this.updateModelValue(profile.id);
		this.onPresentationProfileSelect(profile);
		this.validateProfile(profile);
		this.validateFn();
	}

	private onPresentationProfileSelect(profile: IPresentationProfile): void {
		this.presentationProfileSelected.emit({
			profile
		});
	}

	private validateSelectedProfile() {
		const selectedProfile = this.presentationProfiles?.find(p => p.id === this.value);

		this.validateProfile(selectedProfile);
	}

	private validateProfile(selectedProfile?: IPresentationProfile) {
		const valid = !this.externalErrors?.presentationProfileConflict && (!selectedProfile || selectedProfile.available);
		const control = this.form.control.get('profileFilterAll');

		if (control) {
			toggleError(control, 'presentationProfileConflict', selectedProfile.id && !valid);
			if (!valid) {
				control.markAsDirty();
			}
		}
	}

	private readonly updateSchedule = debounce(() => this.updateScheduleImpl(), 250);
	private updateScheduleImpl(): void {

		if (this.startDate instanceof Date && this.endDate instanceof Date) {
			const startDate = DateUtil.addDays(this.getStartDate(), -1);
			const endDate = DateUtil.addDays(this.endDate, 1);

			if (this.isDateRangeLoaded(startDate, endDate)) {
				return;
			}

			this.loadSchedule(startDate, endDate);
		} else if (!this.profilesLoaded) {
			this.loadSchedule();
		}
	}

	private isDateRangeLoaded(startDate: Date, endDate: Date): boolean {
		return this.loadedScheduleStartDate?.getTime() === startDate.getTime() && this.loadedScheduleEndDate?.getTime() === endDate.getTime();
	}

	private loadSchedule(startDate?: Date, endDate?: Date): void {

		this.status = { loading: true };
		this.profilesLoaded = true;
		this.loadedScheduleStartDate = startDate;
		this.loadedScheduleEndDate = endDate;

		this.EditWebcastService.getVideoSourceSchedule(startDate, endDate)
			.then(videoSources => {
				this.status = { active: true };
				this.presentationProfiles = videoSources
					.map(videoSource => this.readVideoSource(videoSource))
					.reduce((a, b) => a.concat(b), []);
				this.unfilteredProfiles = this.presentationProfiles;

				this.onPresentationProfileSelect(this.presentationProfiles?.find(p => p.id === this.value));
				this.validateSelectedProfile();
			})
			.catch(() => {
				this.status = { error: true };
			});
	}

	private readVideoSource(videoSource: IVideoSource): IPresentationProfile[] {
		let hasConflict = false;
		videoSource.events.forEach(event => {
			if (this.webcastId !== event.id &&
				event.endDate > this.getStartDate() &&
				event.startDate < this.endDate) {

				event.conflict = true;
				hasConflict = true;
			}
		});

		return videoSource.presentationProfiles.filter(profile => {
			profile.available = !hasConflict;
			return profile.isActive;
		});
	}

	private getStartDate(): Date {
		return this.preProductionDurationMs > 0 ?
			new Date(this.startDate.getTime() - this.preProductionDurationMs) :
			this.startDate;
	}

}
